// ── Anular fichajes PENDIENTES propios ────────────────────────────────────
// El trabajador que se equivoca (el caso típico: ha pedido salidas de más)
// puede borrar ÉL MISMO la solicitud desde su propio perfil, pero SOLO
// mientras siga pendiente de aprobación.
//
// Reglas (las cuatro se tienen que cumplir a la vez):
//   1. El fichaje es SUYO: event.workerId === user.id. Un admin actuando
//      sobre otro sigue con sus flujos de siempre (Aprobaciones, ficha del
//      trabajador); esto NO los toca.
//   2. Es una ENTRADA (0) o una SALIDA (1). Pausas, vacaciones, día completo
//      y ausencias por horas tienen sus propios flujos de eliminación.
//   3. approvalStatus === PENDING (0). OJO: null / undefined / '' significa
//      fichaje "en vivo" y en TODO el proyecto cuenta como APROBADO (ver
//      isPendingApproval en services/feedbackMessages.js), así que no entra.
//   4. Tiene id (sin id no hay DELETE que valga).
//
// Un fichaje APROBADO o RECHAZADO no se BORRA desde el perfil: el registro de
// jornada es un documento legal (art. 34.9 ET). Sobre uno aprobado el
// trabajador solo puede SOLICITAR la eliminación (`ownEventAction` más abajo)
// o pedir una corrección (CorrectionRequestModal): las dos dejan rastro y las
// aprueba un responsable.
//
// AVISO DE SEGURIDAD: DELETE /api/event/:id del backend NO comprueba dueño ni
// rol. Esta restricción es SOLO de cliente.
//
// Módulo PURO (sin React): testeado en src/utils/__tests__/ownEventCancel.test.js.
// Gemelo en la app: TemControlApp/src/lib/ownEventCancel.js — si se cambia
// una regla aquí, cambiarla allí (no está bajo el guard de check:shared
// porque cada repo lo coloca en su carpeta de lógica pura).

import { EVENT_TYPES, APPROVAL_STATUS } from '../services/api';
import { hasToken } from '../lib/geoInfo';

// Los únicos tipos que el trabajador puede anular por su cuenta.
export const CANCELABLE_EVENT_TYPES = [EVENT_TYPES.CLOCK_IN, EVENT_TYPES.CLOCK_OUT];

const isCancelableType = (eventType) => CANCELABLE_EVENT_TYPES.includes(Number(eventType));

// Estado explícito PENDIENTE. null/''/undefined NO cuenta (es "en vivo" =
// aprobado), y por eso no basta con `Number(x) === 0`: Number(null) es 0.
export const isPendingStatus = (status) => {
  if (status == null || status === '') return false;
  return Number(status) === APPROVAL_STATUS.PENDING;
};

const isRejected = (event) => !!event && event.approvalStatus != null && event.approvalStatus !== ''
  && Number(event.approvalStatus) === APPROVAL_STATUS.REJECTED;

const sameId = (a, b) => a != null && b != null && String(a) === String(b);

// ¿Puede ESTE usuario anular ESTE fichaje desde su propio perfil?
export const canCancelOwnEvent = (user, event) => {
  if (!user || !event) return false;
  if (event.id == null || event.id === '') return false;
  if (!sameId(user.id, event.workerId)) return false;
  if (!isCancelableType(event.eventType)) return false;
  return isPendingStatus(event.approvalStatus);
};

// ── Qué puede hacer el trabajador con SU fichaje ──────────────────────────
// En Inicio solo hacía falta "anular o nada" (ahí solo se ven los días de
// esta semana). En el Informe horario el trabajador ve TODO su histórico, así
// que hay tres desenlaces según el estado del fichaje:
//
//   CANCEL          PENDIENTE  → lo borra él mismo (DELETE), como en Inicio.
//   REQUEST_DELETE  APROBADO   → NO se borra nada: se marca `;reqdelete=1` en
//                                su geoInfo (PUT) y lo resuelve un responsable
//                                desde Aprobaciones. Mientras tanto el fichaje
//                                SIGUE CONTANDO en la jornada y en la bolsa.
//   CANCEL_REQUEST  APROBADO con `;reqdelete=1` → retira su propia solicitud
//                                (PUT con `;reqdelete=0`).
//   NONE            RECHAZADO, de otro, de otro tipo o sin id.
//
// "Aprobado" incluye el fichaje EN VIVO (approvalStatus null/''/undefined):
// en todo el proyecto cuenta como aprobado, así que tampoco se borra solo.
export const OWN_EVENT_ACTION = Object.freeze({
  NONE: 'none',
  CANCEL: 'cancel',
  REQUEST_DELETE: 'request-delete',
  CANCEL_REQUEST: 'cancel-request',
});

// ¿Tiene ya el token `;reqdelete=1` puesto? (mismo token que los periodos,
// ver src/lib/dayStatus.js → isClockDeleteRequest).
export const hasDeleteRequest = (event) => !!event && hasToken(event.geoInfo, 'reqdelete', '1');

export const ownEventAction = (user, event) => {
  if (!user || !event) return OWN_EVENT_ACTION.NONE;
  if (event.id == null || event.id === '') return OWN_EVENT_ACTION.NONE;
  if (!sameId(user.id, event.workerId)) return OWN_EVENT_ACTION.NONE;
  if (!isCancelableType(event.eventType)) return OWN_EVENT_ACTION.NONE;
  // Pendiente: se borra directamente, aunque arrastrara un flag antiguo.
  if (isPendingStatus(event.approvalStatus)) return OWN_EVENT_ACTION.CANCEL;
  if (isRejected(event)) return OWN_EVENT_ACTION.NONE;
  return hasDeleteRequest(event)
    ? OWN_EVENT_ACTION.CANCEL_REQUEST
    : OWN_EVENT_ACTION.REQUEST_DELETE;
};

// Azúcar para la UI (que solo quiere saber si pinta el botón).
export const canRequestOwnEventDelete = (user, event) =>
  ownEventAction(user, event) === OWN_EVENT_ACTION.REQUEST_DELETE;

export const canCancelOwnDeleteRequest = (user, event) =>
  ownEventAction(user, event) === OWN_EVENT_ACTION.CANCEL_REQUEST;

// Qué se queda tocado si se borra `event`, mirando el resto de fichajes DEL
// MISMO DÍA (`dayEvents`, con o sin el propio evento dentro; se descarta por
// id). Los rechazados no cuentan: ya no suman en ningún cálculo.
//
//   · clockOuts     → borrando una ENTRADA, la salida que la cerraba se queda
//                     huérfana ("sin abrir").
//   · pauses        → pausas que colgaban de esa entrada (se cuentan por
//                     INICIO de pausa, no por evento suelto).
//   · leavesDayOpen → borrando una SALIDA, el día vuelve a quedar sin cerrar.
export const cancelSideEffects = (event, dayEvents = []) => {
  const none = { clockOuts: 0, pauses: 0, leavesDayOpen: false };
  if (!event) return none;
  const at = new Date(event.timestamp).getTime();
  if (!Number.isFinite(at)) return none;

  const rest = (Array.isArray(dayEvents) ? dayEvents : [])
    .filter((e) => e && !sameId(e.id, event.id) && !isRejected(e))
    .map((e) => ({ e, at: new Date(e.timestamp).getTime() }))
    .filter((x) => Number.isFinite(x.at))
    .sort((a, b) => a.at - b.at);

  const type = Number(event.eventType);

  if (type === EVENT_TYPES.CLOCK_IN) {
    // Tramo que abría esta entrada: hasta su salida (incluida) o hasta la
    // siguiente entrada, que ya abriría otro tramo distinto.
    let clockOuts = 0;
    let pauses = 0;
    for (const { e, at: eAt } of rest) {
      if (eAt < at) continue;
      const kind = Number(e.eventType);
      if (kind === EVENT_TYPES.CLOCK_IN) break;
      if (kind === EVENT_TYPES.CLOCK_OUT) { clockOuts += 1; break; }
      if (kind === EVENT_TYPES.PAUSE_START) pauses += 1;
    }
    return { clockOuts, pauses, leavesDayOpen: false };
  }

  if (type === EVENT_TYPES.CLOCK_OUT) {
    // Se recorre el día SIN el evento que se va a borrar: si al final queda
    // una entrada sin cerrar, el día vuelve a estar "en curso".
    let open = false;
    for (const { e } of rest) {
      const kind = Number(e.eventType);
      if (kind === EVENT_TYPES.CLOCK_IN) open = true;
      else if (kind === EVENT_TYPES.CLOCK_OUT) open = false;
    }
    return { clockOuts: 0, pauses: 0, leavesDayOpen: open };
  }

  return none;
};

// Aviso en castellano de lo que se lleva por delante el borrado, para la
// confirmación (Alert en la app, ConfirmDialog en la web). '' si no hay nada
// que avisar.
export const cancelWarningText = (event, dayEvents = []) => {
  const fx = cancelSideEffects(event, dayEvents);
  const parts = [];
  if (fx.clockOuts > 0) {
    parts.push('La salida de ese día se quedará sin su entrada y dejará de contar: anúlala también o pide una corrección.');
  }
  if (fx.pauses > 0) {
    parts.push(fx.pauses === 1
      ? 'La pausa de ese día se quedará fuera de jornada.'
      : `Las ${fx.pauses} pausas de ese día se quedarán fuera de jornada.`);
  }
  if (fx.leavesDayOpen) {
    parts.push('El día volverá a quedar SIN CERRAR hasta que registres la salida correcta.');
  }
  return parts.join(' ');
};

// Mismo aviso, pero para la SOLICITUD de eliminación de un fichaje aprobado:
// los efectos no son inmediatos (pasan cuando el responsable la apruebe), así
// que el texto va en futuro y con esa condición delante. '' si no hay nada.
export const deleteRequestWarningText = (event, dayEvents = []) => {
  const warn = cancelWarningText(event, dayEvents);
  return warn ? `Si el responsable la aprueba: ${warn}` : '';
};
